import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useWorkflows } from '../../lib/workflows/useWorkflows';
import { DemographicsGrid } from '../capture/DemographicsFields';

interface Parsed {
  captureType: string;
  fields: Record<string, string>;
  editable: boolean;
  blockingRecordTypes: number[];
}

/** Upload an existing .eft, correct its descriptive data, and download the fixed file. No storage needed. */
export function CorrectEft() {
  const workflows = useWorkflows();
  const [key, setKey] = useState<string | null>(null);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  /*
   * Set when the file cannot be written back at all — its record structure is damaged, so there is nothing
   * to correct. The server says so with EFT_UNWRITABLE and hands back the file's own TCN and capture type,
   * which is everything needed to offer the path that DOES work: import it and rebuild.
   */
  const [recover, setRecover] = useState<{ tcn?: string; captureType?: string } | null>(null);

  const wf = workflows.data?.find((w) => w.captureType === parsed?.captureType);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { url, key: k } = await api.post<{ url: string; key: string }>('/uploads/presign', {
        contentType: 'application/octet-stream', ext: 'eft',
      });
      const put = await fetch(url, { method: 'PUT', headers: { 'content-type': 'application/octet-stream' }, body: file });
      if (!put.ok) throw new Error('Upload failed');
      const p = await api.post<Parsed>('/eft/parse', { key: k });
      return { key: k, parsed: p };
    },
    onSuccess: ({ key: k, parsed: p }) => { setErr(null); setKey(k); setParsed(p); setValues(p.fields); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Could not read that file'),
  });

  const correct = useMutation({
    mutationFn: () => api.post<{ url: string; filename: string }>('/eft/correct', { key, fields: values }),
    onSuccess: (r) => {
      setErr(null);
      const a = document.createElement('a');
      a.href = r.url; a.download = r.filename; a.rel = 'noopener';
      document.body.appendChild(a); a.click();
      setTimeout(() => a.remove(), 1500);
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : 'Could not produce the corrected file');
      const body = e instanceof ApiError ? (e.body as { code?: string; tcn?: string; captureType?: string } | undefined) : undefined;
      setRecover(body?.code === 'EFT_UNWRITABLE' ? { ...(body.tcn ? { tcn: body.tcn } : {}), ...(body.captureType ? { captureType: body.captureType } : {}) } : null);
    },
  });

  function reset() { setKey(null); setParsed(null); setValues({}); setErr(null); setRecover(null); upload.reset(); correct.reset(); }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Correct an EFT file</h1>
          <div className="sub">Upload an existing .eft, fix its descriptive data, and download the corrected file. Fingerprints are left untouched.</div>
        </div>
        <Link to="/app/transactions" className="btn btn-ghost">← Transactions</Link>
      </div>

      {!parsed ? (
        <div className="panel">
          <label className="dropzone">
            <input type="file" accept=".eft,.ebts,application/octet-stream" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); }} />
            <span className="dz-icon">⭳</span>
            <span>{upload.isPending ? 'Reading…' : 'Click to upload an .eft file'}</span>
            <span className="dz-sub">We read the descriptive fields; fingerprint images stay as-is.</span>
          </label>
          {err && <p className="cart-error">{err}</p>}
        </div>
      ) : !parsed.editable ? (
        <div className="panel empty">
          <h3>This EFT can't be corrected</h3>
          <p>It contains record type(s) {parsed.blockingRecordTypes.join(', ')} that we can't safely rewrite yet.</p>
          <button className="btn btn-ghost" onClick={reset}>Try another file</button>
        </div>
      ) : !wf ? (
        <div className="panel empty"><h3>Unknown workflow</h3><p>No form configured for “{parsed.captureType}”. <button className="linklike" onClick={reset}>Try another file</button></p></div>
      ) : (
        <div className="panel">
          <DemographicsGrid
            fields={wf.fields}
            specification={wf.specification}
            values={values}
            onChange={(name, v) => setValues((prev) => ({ ...prev, [name]: v }))}
          />
          {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
          {/*
            * Correcting is impossible for this file, so the only useful thing to show is the path that works.
            * Importing only READS the file: the fingerprints and the demographics it still holds come across,
            * and the rebuild carries the original's TCN so it is not charged for a second time.
            */}
          {recover && (
            <div className="banner-warn" style={{ marginTop: 12 }}>
              <p style={{ margin: '0 0 8px' }}>
                Its fingerprints are still readable. Import the file into a new transaction instead — you will
                only re-enter the details it lost, and the rebuild is not charged again.
              </p>
              <Link
                className="btn btn-primary btn-sm"
                to={`/app/capture/${recover.captureType ?? 'atf4'}${recover.tcn ? `?rebuildOf=${encodeURIComponent(recover.tcn)}` : ''}`}
              >
                Import and rebuild →
              </Link>
            </div>
          )}
          <div className="flow-actions">
            <button className="btn btn-ghost" onClick={reset}>Start over</button>
            <button className="btn btn-primary" disabled={correct.isPending} onClick={() => correct.mutate()}>
              {correct.isPending ? 'Preparing…' : '⤓ Download corrected .eft'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
